import {useMemo} from 'react';
import {Concept, DerivedConcept} from 'types';
import type {TableConfiguration} from 'schema/table/types';
import type {Schema} from 'schemaDefinition/types';
import type {WorkHit} from 'services/elastic/types';
import {useCodelists} from 'services/codelists';
import {
  mapWorkHitToWorkSummary,
  useMetadataCodelistIds,
} from 'services/metadata';
import {useSchema} from 'schemas';
import {useSchemaReference} from 'schemas/hooks/useSchemaReference';
import {useTableConfiguration} from 'schemas/hooks/useTableConfiguration';
import type {
  LinkedValueSearchFieldConfig,
  SearchResultType,
  SearchResultValue,
  TableSearchResultValue,
} from '../types';

/**
 * Maps data for table presentation, i.e. maps nested work (with expressions and manifestations)
 * to work summary with aggregated values (MainTitles from first manifestation)
 * Returns mapped data, and corresponding schema and table configuration
 */
export const useTablePresentation = (
  searchEntityType: SearchResultType,
  searchResult: SearchResultValue[],
  schema: Schema,
  sourceConfig?: LinkedValueSearchFieldConfig,
): {
  tableSearchResult: TableSearchResultValue[];
  tableSchema: Schema;
  tableConfiguration: TableConfiguration;
} => {
  const resolvedEntity =
    searchEntityType === Concept.work
      ? DerivedConcept.workSummary
      : searchEntityType;
  const workSummarySchema = useSchema(DerivedConcept.workSummary);
  const codelistIds = useMetadataCodelistIds();
  const codelists = useCodelists(codelistIds);
  const schemaRef = useSchemaReference(resolvedEntity);
  const tableConfiguration = useTableConfiguration(schemaRef);

  return useMemo(() => {
    const indicateRowId =
      (searchEntityType as string) === sourceConfig?.entity
        ? sourceConfig?.sourceEntityId
        : undefined;

    const resolvedTableConfiguration = indicateRowId
      ? {
          ...tableConfiguration,
          indicateRowId,
        }
      : tableConfiguration;

    if (searchEntityType === Concept.work) {
      return {
        tableSearchResult: searchResult
          /*
           If searchResult has not changed after entityType was changed, the content is old (e.g. agents)
           */
          .filter(r => (r as WorkHit).work)
          .map(work => mapWorkHitToWorkSummary(work as WorkHit, codelists)),
        tableSchema: workSummarySchema,
        tableConfiguration: resolvedTableConfiguration,
      };
    }

    return {
      tableSearchResult: searchResult as TableSearchResultValue[],
      tableSchema: schema,
      tableConfiguration: resolvedTableConfiguration,
    };
  }, [
    searchEntityType,
    sourceConfig?.entity,
    sourceConfig?.sourceEntityId,
    tableConfiguration,
    searchResult,
    schema,
    workSummarySchema,
    codelists,
  ]);
};
